import type { TypePolicies } from '@apollo/client'
import type {
  BookAsNodeConnection,
  ViewedBookAsNodeConnection
} from 'api/data.types'

export const typePolicies: TypePolicies = {
  Query: {
    fields: {
      searchBookV3: {
        keyArgs: ['searchTerm', 'opts'],
        merge(
          existing = { books: [], searchFilters: [] },
          incoming = { books: [], searchFilters: [] },
          { args }
        ) {
          const isFirstPage = args?.page === 1

          if (isFirstPage) {
            return incoming
          }

          const mergedCache = {
            books: [...existing.books, ...incoming.books],
            searchFilters: incoming.searchFilters
          }

          return mergedCache
        }
      },
      searchArticles: {
        keyArgs: ['input', ['term']],
        merge(existing = [], incoming = [], { args }) {
          const isFirstPage = args?.input.page === 1

          if (isFirstPage) {
            return incoming
          }

          const mergedCache = [...existing, ...incoming]

          return mergedCache
        }
      },
      searchAllAvailableBooks: {
        keyArgs: ['searchTerm'],
        merge(
          existing: { __ref: string }[],
          incoming: { __ref: string }[],
          { args }
        ) {
          const { page } = args
          const isFirstPage = page === 1

          if (isFirstPage) {
            return incoming
          }
          const filteredIncomingBooks = incoming.filter(incomingBook => {
            const bookResult = existing.reduce((acc, existingBook) => {
              if (existingBook.__ref === incomingBook.__ref) {
                return false
              }
              return acc
            }, true)
            return bookResult
          })
          const mergedCache = [...existing, ...filteredIncomingBooks]

          return mergedCache
        }
      }
    }
  },
  Reader: {
    fields: {
      relayPaginatedFavorites: {
        keyArgs: false,
        merge(
          existing: BookAsNodeConnection | undefined,
          incoming: BookAsNodeConnection,
          { args }
        ) {
          const isFirstPage = !args?.after
          if (isFirstPage) {
            return incoming
          }
          const filteredIncomingEdges = incoming?.edges?.filter(
            incomingEdge => {
              return !existing?.edges
                ?.map(edge => edge.cursor)
                .includes(incomingEdge.cursor)
            }
          )
          const mergedCache = {
            pageInfo: incoming.pageInfo,
            edges: [
              ...(existing?.edges || []),
              ...(filteredIncomingEdges || [])
            ]
          } as BookAsNodeConnection

          return mergedCache
        }
      },
      relayPaginatedViewedBooks: {
        keyArgs: ['isReading', 'contentFilter', 'order', 'first'],
        merge(
          existing: ViewedBookAsNodeConnection | undefined,
          incoming: ViewedBookAsNodeConnection,
          { args }
        ) {
          const isFirstPage = !args?.after
          if (isFirstPage) {
            return incoming
          }

          const existingCursors = new Set(
            existing?.edges?.map(edge => edge.cursor)
          )
          const filteredIncomingEdges = incoming?.edges?.filter(
            incomingEdge => !existingCursors.has(incomingEdge.cursor)
          )

          const mergedCache = {
            pageInfo: incoming.pageInfo,
            edges: [
              ...(existing?.edges || []),
              ...(filteredIncomingEdges || [])
            ]
          } as ViewedBookAsNodeConnection

          return mergedCache
        }
      },
      overallRanking: {
        keyArgs: false,
        merge(existing, incoming, { mergeObjects }) {
          return mergeObjects(existing, incoming)
        }
      }
    }
  },
  Teacher: {
    fields: {
      availableBooks: {
        keyArgs: ['perPage', 'readerId'],
        merge(
          existing: { __ref: string }[],
          incoming: { __ref: string }[],
          { args }
        ) {
          const isFirstPage = args?.page === 1

          if (isFirstPage) {
            return incoming
          }

          const filteredIncomingBooks = incoming.filter(incomingBook => {
            const bookResult = existing.reduce((acc, existingBook) => {
              if (existingBook.__ref === incomingBook.__ref) {
                return false
              }
              return acc
            }, true)
            return bookResult
          })

          const mergedCache = [...existing, ...filteredIncomingBooks]

          return mergedCache
        }
      }
    }
  },
  BookAsNode: {
    fields: {
      favoritesVisibility: {
        read(existing) {
          return existing === undefined || existing === true
        }
      }
    }
  },
  Forest: {
    fields: {
      waterDropDetails: {
        keyArgs: false,
        merge(existing, incoming, { mergeObjects }) {
          return mergeObjects(existing, incoming)
        }
      }
    }
  }
}
